import { ref } from 'vue';
import { useHttpRequest } from '~/composables/useHttpRequest';
import { useAuthService } from '~/composables/useAuthService';
import { endpoints } from '~/network/endpoints.js';

export function useLeaveEntity() {
  const { httpRequest } = useHttpRequest();
  const { user, updateLocalUser } = useAuthService();
  const { setAlertMessage } = useAlertService();

  const leavePending = ref(false);
  const leaveErrors = ref({});

  /**
   * Leave a club
   * @param {string} clubId - The club ID to leave
   * @returns {Promise<Object>} - Updated user object
   */
  const leaveClub = async (clubId) => {
    leavePending.value = true;
    leaveErrors.value = {};

    try {
      const updatedUser = await httpRequest(endpoints.clubs.leave(clubId), {});

      // Update local user object
      await updateLocalUser();

      setAlertMessage({
        message: 'You have left the club',
        color: 'success'
      });

      return updatedUser;
    } catch (error) {
      console.error('Error leaving club:', error);
      leaveErrors.value = error;

      const errorMessage = error?.global?.[0] || error?.message || 'Failed to leave club';
      setAlertMessage({
        message: errorMessage,
        color: 'error'
      });

      throw error;
    } finally {
      leavePending.value = false;
    }
  };

  /**
   * Leave a ladder
   * @param {string} ladderId - The ladder ID to leave
   * @returns {Promise<Object>} - Updated user object
   */
  const leaveLadder = async (ladderId) => {
    leavePending.value = true;
    leaveErrors.value = {};

    try {
      const updatedUser = await httpRequest(endpoints.ladders.leave(ladderId), {});

      // Update local user object
      await updateLocalUser();

      setAlertMessage({
        message: 'You have left the ladder',
        color: 'success'
      });

      return updatedUser;
    } catch (error) {
      console.error('Error leaving ladder:', error);
      leaveErrors.value = error;

      const errorMessage = error?.global?.[0] || error?.message || 'Failed to leave ladder';
      setAlertMessage({
        message: errorMessage,
        color: 'error'
      });

      throw error;
    } finally {
      leavePending.value = false;
    }
  };

  /**
   * Leave a league cycle division
   * @param {string} leagueCycleDivisionId - The league cycle division ID to leave
   * @returns {Promise<Object>} - Updated user object
   */
  const leaveLeagueCycleDivision = async (leagueCycleDivisionId) => {
    leavePending.value = true;
    leaveErrors.value = {};

    try {
      const updatedUser = await httpRequest(endpoints.leagueCycleDivisions.leave(leagueCycleDivisionId), {});

      // Update local user object
      await updateLocalUser();

      setAlertMessage({
        message: 'You have left the box league',
        color: 'success'
      });

      return updatedUser;
    } catch (error) {
      console.error('Error leaving league cycle division:', error);
      leaveErrors.value = error;

      const errorMessage = error?.global?.[0] || error?.message || 'Failed to leave box league';
      setAlertMessage({
        message: errorMessage,
        color: 'error'
      });

      throw error;
    } finally {
      leavePending.value = false;
    }
  };

  /**
   * Leave a meet (E5: future RSVPs are cancelled — cancellation tiers apply
   * provisionally where inside a window; waived if the spot refills)
   * @param {string} meetId - The meet ID to leave
   * @returns {Promise<Object>} - Updated meet detail response
   */
  const leaveMeet = async (meetId) => {
    leavePending.value = true;
    leaveErrors.value = {};

    try {
      const res = await httpRequest(endpoints.meets.leave(meetId), {});

      // Update local user object
      await updateLocalUser();

      setAlertMessage({
        message: 'You have left the meet',
        color: 'success'
      });

      return res;
    } catch (error) {
      console.error('Error leaving meet:', error);
      leaveErrors.value = error;

      const errorMessage = error?.global?.[0] || error?.message || 'Failed to leave meet';
      setAlertMessage({
        message: errorMessage,
        color: 'error'
      });

      throw error;
    } finally {
      leavePending.value = false;
    }
  };

  return {
    leaveClub,
    leaveLadder,
    leaveLeagueCycleDivision,
    leaveMeet,
    leavePending,
    leaveErrors
  };
}
